import { useRef, useState } from 'react';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { Icon, type IconName } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PatientFormModal } from '@/components/practitioner/PatientForm/PatientFormModal';
import { AddOwnerDialog } from './AddOwnerDialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PatientHeaderActions } from './PatientHeaderActions';
import { PetChangeRequestSection } from './PetChangeRequestSection';
import { OverviewTab } from './tabs/OverviewTab';
import { SessionNotesTab } from './tabs/SessionNotesTab';
import { TreatmentsTab } from './tabs/TreatmentsTab';
import { TimelineTab } from './tabs/TimelineTab';
import { FilesTab } from './tabs/FilesTab';
import { useClientQuery } from '@/hooks/useClientQuery';
import { usePatientQuery } from '@/hooks/usePatientsQueries';
import { usePatientSessionNotesQuery } from '@/hooks/useSessionNoteQueries';
import { useConsultationOutcomeOptionsQuery } from '@/hooks/useConsultationConfig';
import { useHasWorkspaceModule } from '@/hooks/useHasWorkspaceModule';
import { useTabSearchParam } from '@/hooks/useTabSearchParam';
import { MODULE_KEY } from '@/constants/modules';
import { MEDIA_IMAGE_CONTENT_TYPES, buildAcceptAttribute } from '@/constants/media';
import {
  useArchivePatientMutation,
  useRemovePatientPhotoMutation,
  useRestorePatientMutation,
  useUpdatePatientStatusMutation,
  useUploadPatientPhotoMutation,
} from '@/hooks/usePatientMutations';
import { buildTherapyTypeTranslationKey } from '@/constants/appointmentOptions';
import { PATIENT_CASE_STATUS_OPTIONS } from '@/constants/patients';
import { formatPatientAge } from '@/utils/patientAge';
import { formatIsoDateToDayAndMonth } from '@/utils/dateFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { formatPhoneNumberForDisplay } from '@/utils/phoneNumbers';
import { AssistantAskButton } from '@/components/assistant/AssistantAskButton';
import { AssistantTargetRegion } from '@/components/assistant/uiActions/AssistantTargetRegion';
import { ASSISTANT_TARGET_KEY } from '@/components/assistant/uiActions/assistantTargetKeys';

function localizedOutcomeLabel(labelEn: string, labelPl: string): string {
  if (i18next.language === 'pl' && labelPl.length > 0) {
    return labelPl;
  }
  return labelEn;
}

export function PatientDetailPage() {
  const { t } = useTranslation(['patients', 'common', 'assistant']);
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { patientId } = useParams<{ patientId: string }>();
  const resolvedId = patientId === undefined ? '' : patientId;
  const patientQuery = usePatientQuery(resolvedId);
  const patient = patientQuery.data;
  const ownerClientId = patient === undefined || patient.clientId === null ? '' : patient.clientId;
  const clientQuery = useClientQuery(ownerClientId);
  const outcomeOptionsQuery = useConsultationOutcomeOptionsQuery(
    patient !== undefined && patient.treatmentClearance !== null
  );
  const archiveMutation = useArchivePatientMutation();
  const restoreMutation = useRestorePatientMutation();
  const statusMutation = useUpdatePatientStatusMutation();
  const uploadPhoto = useUploadPatientPhotoMutation();
  const removePhoto = useRemovePatientPhotoMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasFirstConsultation = useHasWorkspaceModule(MODULE_KEY.firstConsultation);
  const hasTreatmentPlans = useHasWorkspaceModule(MODULE_KEY.treatmentPlans);
  const hasSessionNotes = useHasWorkspaceModule(MODULE_KEY.sessionNotes);
  const hasFiles = useHasWorkspaceModule(MODULE_KEY.files);
  const draftSessionNotesQuery = usePatientSessionNotesQuery(
    hasSessionNotes && patient !== undefined ? patient.id : '',
    1,
    'draft'
  );
  const draftSessionNoteCount =
    draftSessionNotesQuery.data === undefined ? 0 : draftSessionNotesQuery.data.totalItems;

  function openDraftSessionNotes() {
    setSearchParams(function updateParams(currentParams) {
      const nextParams = new URLSearchParams(currentParams);
      nextParams.set('tab', 'sessionNotes');
      nextParams.set('status', 'draft');
      return nextParams;
    });
  }

  const visibleTabs = [
    { id: 'overview', label: t('patients:tabs.overview') },
    ...(hasSessionNotes ? [{ id: 'sessionNotes', label: t('patients:tabs.sessionNotes') }] : []),
    ...(hasTreatmentPlans || hasSessionNotes
      ? [{ id: 'treatments', label: t('patients:tabs.treatments') }]
      : []),
    { id: 'timeline', label: t('patients:tabs.timeline') },
    ...(hasFiles ? [{ id: 'files', label: t('patients:tabs.files') }] : []),
  ];
  const [activeTabId, setActiveTabId] = useTabSearchParam(
    visibleTabs.map(function toId(tab) {
      return tab.id;
    }),
    'overview'
  );

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAddOwnerOpen, setIsAddOwnerOpen] = useState(false);
  const [isConfirmingArchive, setIsConfirmingArchive] = useState(false);

  const isArchived = patient !== undefined && patient.status === 'archived';
  const ageLabel = patient === undefined ? null : formatPatientAge(patient.dateOfBirth, t);
  const outcomeOption =
    patient === undefined ||
    patient.treatmentClearance === null ||
    outcomeOptionsQuery.data === undefined
      ? undefined
      : outcomeOptionsQuery.data.find(function match(option) {
          return option.code === patient.treatmentClearance;
        });
  const outcomeLabel =
    outcomeOption === undefined
      ? null
      : localizedOutcomeLabel(outcomeOption.labelEn, outcomeOption.labelPl);

  function selectPhoto() {
    if (fileInputRef.current !== null) {
      fileInputRef.current.click();
    }
  }

  function handlePhotoSelected(changeEvent: React.ChangeEvent<HTMLInputElement>) {
    const files = changeEvent.target.files;
    if (files === null || patient === undefined) {
      return;
    }
    const selected = files.item(0);
    if (selected === null) {
      return;
    }
    uploadPhoto.mutate(
      { patientId: patient.id, file: selected },
      {
        onSuccess: function done() {
          showSuccessNotification(t('patients:detail.photoUpdated'));
        },
        onError: function fail(error) {
          const message = resolveApiErrorMessage(error);
          showErrorNotification(message.length > 0 ? message : t('patients:detail.photoError'));
        },
      }
    );
    changeEvent.target.value = '';
  }

  function confirmArchive() {
    if (patient === undefined) {
      return;
    }
    archiveMutation.mutate(patient.id, {
      onSuccess: function done() {
        showSuccessNotification(t('patients:detail.archived'));
        setIsConfirmingArchive(false);
      },
      onError: function fail(error) {
        showErrorNotification(resolveApiErrorMessage(error));
        setIsConfirmingArchive(false);
      },
    });
  }

  function changeStatus(nextStatus: string) {
    if (patient === undefined) {
      return;
    }
    statusMutation.mutate(
      { patientId: patient.id, status: nextStatus },
      {
        onSuccess: function done() {
          showSuccessNotification(t('patients:detail.statusUpdated'));
        },
        onError: function fail(error) {
          showErrorNotification(resolveApiErrorMessage(error));
        },
      }
    );
  }

  function restore() {
    if (patient === undefined) {
      return;
    }
    restoreMutation.mutate(patient.id, {
      onSuccess: function done() {
        showSuccessNotification(t('patients:detail.restored'));
      },
      onError: function fail(error) {
        showErrorNotification(resolveApiErrorMessage(error));
      },
    });
  }

  return (
    <div className="p-6 md:p-8 [container-type:inline-size]">
      <button
        type="button"
        onClick={function back() {
          navigate(APPLICATION_ROUTES.workspacePatients);
        }}
        className="flex items-center gap-1.5 min-h-6 text-sm text-zoomuted hover:text-zooprimary transition-colors mb-4"
      >
        <Icon name="ArrowLeft" />
        {t('patients:detail.back')}
      </button>

      {patientQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : patientQuery.isError === true || patient === undefined ? (
        <QueryErrorState
          error={patientQuery.error}
          onRetry={function retry() {
            patientQuery.refetch();
          }}
        />
      ) : (
        <>
          <AssistantTargetRegion
            targetKey={ASSISTANT_TARGET_KEY.patientHeader}
            entityId={patient.id}
            className="mb-5"
          >
            <Card>
              <div className="flex items-start gap-4 flex-wrap">
                <PatientPhoto
                  photoUrl={patient.photoUrl}
                  name={patient.name}
                  onSelect={selectPhoto}
                  onRemove={function remove() {
                    removePhoto.mutate(patient.id, {
                      onSuccess: function done() {
                        showSuccessNotification(t('patients:detail.photoRemoved'));
                      },
                    });
                  }}
                  isBusy={uploadPhoto.isPending || removePhoto.isPending}
                />
                <div className="flex-1 basis-56 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-bold text-zooindigo break-words">
                      {patient.name}
                    </h1>
                    {patient.sex === null ? null : (
                      <Badge tone="neutral">{t('common:sex.' + patient.sex)}</Badge>
                    )}
                    {patient.status === 'paused' || patient.status === 'completed' ? (
                      <Badge tone="neutral" className="text-zooviolet">
                        {t('patients:status.' + patient.status)}
                      </Badge>
                    ) : null}
                    {isArchived === true ? (
                      <Badge tone="muted">{t('patients:detail.archivedBadge')}</Badge>
                    ) : null}
                    <AssistantAskButton
                      subject={patient.name}
                      question={t('assistant:askAbout.patient', { name: patient.name })}
                    />
                  </div>
                  <p className="text-sm text-zoomuted mt-0.5">
                    {[patient.species, patient.breed]
                      .filter(function keep(part) {
                        return part !== null && part.trim().length > 0;
                      })
                      .join(' · ')}
                    {ageLabel === null ? '' : ' · ' + ageLabel}
                  </p>
                  {patient.clientId === null ? (
                    <p className="text-xs text-zoomuted mt-1 flex items-center gap-1.5 flex-wrap">
                      <Icon name="User" />
                      {t('patients:owner.none')}
                      {isArchived === true ? null : (
                        <button
                          type="button"
                          onClick={function openAddOwner() {
                            setIsAddOwnerOpen(true);
                          }}
                          className="font-bold text-zooprimary hover:underline"
                        >
                          {t('patients:owner.addAction')}
                        </button>
                      )}
                    </p>
                  ) : clientQuery.data === undefined ? null : (
                    <p className="text-xs text-zoomuted mt-1 flex items-center gap-1">
                      <Icon name="User" />
                      {clientQuery.data.fullName}
                      {clientQuery.data.phone === null
                        ? ''
                        : ' · ' + formatPhoneNumberForDisplay(clientQuery.data.phone)}
                    </p>
                  )}
                  {draftSessionNoteCount === 0 ? null : (
                    <button
                      type="button"
                      onClick={openDraftSessionNotes}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-zooerrorbg px-3 py-1 text-xs font-bold text-zooerrortext hover:underline"
                    >
                      <Icon name="AlertCircle" />
                      {t('patients:detail.draftNotesLink', { count: draftSessionNoteCount })}
                    </button>
                  )}
                  {patient.warnings.length === 0 ? null : (
                    <div className="mt-3 rounded-xl bg-zoowarningbg px-3 py-2">
                      <div className="flex items-center gap-1.5 text-zoowarningtext">
                        <Icon name="AlertCircle" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">
                          {t('patients:detail.safetyWarnings')}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {patient.warnings.map(function renderWarning(label) {
                          return (
                            <Badge key={label} tone="warning" outline>
                              {label}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <PatientHeaderActions
                  isArchived={isArchived}
                  hasFirstConsultation={hasFirstConsultation}
                  hasTreatmentPlans={hasTreatmentPlans}
                  hasSessionNotes={hasSessionNotes}
                  onRestore={restore}
                  onFirstConsultation={function newConsultation() {
                    navigate(
                      generatePath(APPLICATION_ROUTES.workspacePatientFirstConsultation, {
                        patientId: patient.id,
                        consultationId: 'new',
                      })
                    );
                  }}
                  onTreatmentPlan={function openTreatmentPlan() {
                    navigate(
                      generatePath(APPLICATION_ROUTES.workspacePatientTreatmentPlan, {
                        patientId: patient.id,
                        planId: 'current',
                      })
                    );
                  }}
                  onSessionNote={function newNote() {
                    navigate(
                      generatePath(APPLICATION_ROUTES.workspacePatientSessionNote, {
                        patientId: patient.id,
                        noteId: 'new',
                      })
                    );
                  }}
                  onEdit={function edit() {
                    setIsEditOpen(true);
                  }}
                  onArchive={function archive() {
                    setIsConfirmingArchive(true);
                  }}
                />
              </div>

              <div className="mt-4 pt-4 border-t border-zooborder grid gap-3 [@container(min-width:40rem)]:grid-cols-3">
                {isArchived === true ? null : (
                  <StatusMetaField
                    value={patient.status}
                    isDisabled={statusMutation.isPending}
                    onChange={changeStatus}
                  />
                )}
                <MetaItem
                  icon="Activity"
                  label={t('patients:detail.diagnosis')}
                  value={
                    patient.diagnosis === null
                      ? t('patients:detail.notRecorded')
                      : patient.diagnosis
                  }
                  isMuted={patient.diagnosis === null}
                />
                <MetaItem
                  icon="Calendar"
                  label={t('patients:detail.nextVisit')}
                  value={
                    patient.nextVisit === null
                      ? t('patients:detail.nextVisitNone')
                      : formatIsoDateToDayAndMonth(patient.nextVisit.date) +
                        ' · ' +
                        patient.nextVisit.startTime +
                        ' · ' +
                        t(buildTherapyTypeTranslationKey(patient.nextVisit.therapyType))
                  }
                  isHighlight={patient.nextVisit !== null}
                  isMuted={patient.nextVisit === null}
                />
                <MetaItem
                  icon="Clock"
                  label={t('patients:detail.lastVisit')}
                  value={
                    patient.lastVisit === null
                      ? t('patients:detail.lastVisitNone')
                      : formatIsoDateToDayAndMonth(patient.lastVisit.date) +
                        ' · ' +
                        patient.lastVisit.startTime +
                        ' · ' +
                        (patient.lastVisit.therapyType === null
                          ? t('patients:detail.lastVisitDirectSession')
                          : t(buildTherapyTypeTranslationKey(patient.lastVisit.therapyType)))
                  }
                  isMuted={patient.lastVisit === null}
                />
                <MetaItem
                  icon="Clipboard"
                  label={t('patients:detail.activePlan')}
                  value={
                    patient.activePlan === null
                      ? t('patients:detail.activePlanNone')
                      : t('patients:detail.activePlanVersion', {
                          version: patient.activePlan.version,
                        }) +
                        ' · ' +
                        formatIsoDateToDayAndMonth(patient.activePlan.createdAt)
                  }
                  isHighlight={patient.activePlan !== null}
                  isMuted={patient.activePlan === null}
                />
                {outcomeLabel === null ? null : (
                  <MetaItem
                    icon="ClipboardCheck"
                    label={t('patients:detail.firstConsultationOutcome')}
                    value={outcomeLabel}
                    isHighlight
                  />
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={buildAcceptAttribute(MEDIA_IMAGE_CONTENT_TYPES)}
                className="hidden"
                onChange={handlePhotoSelected}
              />
            </Card>
          </AssistantTargetRegion>

          <PetChangeRequestSection patientId={patient.id} />

          <Tabs value={activeTabId} onValueChange={setActiveTabId} idPrefix="patient-detail">
            <AssistantTargetRegion
              targetKey={ASSISTANT_TARGET_KEY.patientRecordTabs}
              entityId={patient.id}
              className="mb-5"
            >
              <TabsList ariaLabel={t('patients:detail.recordSections')}>
                {visibleTabs.map(function renderTab(tab) {
                  return (
                    <TabsTrigger key={tab.id} value={tab.id}>
                      {tab.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </AssistantTargetRegion>

            <TabsContent value="overview">
              <OverviewTab
                patient={patient}
                hasFirstConsultation={hasFirstConsultation}
                hasSessionNotes={hasSessionNotes}
              />
            </TabsContent>

            {hasSessionNotes === false ? null : (
              <TabsContent value="sessionNotes">
                <SessionNotesTab patientId={patient.id} isArchived={isArchived} />
              </TabsContent>
            )}

            {hasTreatmentPlans === false && hasSessionNotes === false ? null : (
              <TabsContent value="treatments">
                <TreatmentsTab patientId={patient.id} hasTreatmentPlans={hasTreatmentPlans} />
              </TabsContent>
            )}

            <TabsContent value="timeline">
              <TimelineTab patientId={patient.id} />
            </TabsContent>

            {hasFiles === false ? null : (
              <TabsContent value="files">
                <FilesTab patientId={patient.id} />
              </TabsContent>
            )}
          </Tabs>
        </>
      )}

      {isEditOpen === true && patient !== undefined ? (
        <PatientFormModal
          clientId={patient.clientId}
          patient={patient}
          onClose={function close() {
            setIsEditOpen(false);
          }}
        />
      ) : null}

      {isAddOwnerOpen === true && patient !== undefined ? (
        <AddOwnerDialog
          patientId={patient.id}
          patientName={patient.name}
          onClose={function closeAddOwner() {
            setIsAddOwnerOpen(false);
          }}
        />
      ) : null}

      {isConfirmingArchive === true && patient !== undefined ? (
        <ConfirmDialog
          tone="danger"
          iconName="Trash"
          title={t('patients:detail.archiveTitle')}
          description={t('patients:detail.archiveDescription', { name: patient.name })}
          confirmLabel={t('patients:detail.archive')}
          isConfirming={archiveMutation.isPending}
          onConfirm={confirmArchive}
          onCancel={function cancel() {
            setIsConfirmingArchive(false);
          }}
        />
      ) : null}
    </div>
  );
}

interface PatientPhotoProps {
  photoUrl: string | null;
  name: string;
  onSelect: () => void;
  onRemove: () => void;
  isBusy: boolean;
}

function PatientPhoto({ photoUrl, name, onSelect, onRemove, isBusy }: PatientPhotoProps) {
  const { t } = useTranslation('patients');
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="w-20 h-20 rounded-2xl bg-zoovpalelavender flex items-center justify-center overflow-hidden shrink-0">
        {photoUrl === null ? (
          <Icon name="PawPrint" className="text-zooprimary text-xl" />
        ) : (
          <img src={photoUrl} alt={name} className="w-full h-full object-cover" />
        )}
      </div>
      <button
        type="button"
        disabled={isBusy}
        aria-busy={isBusy === true ? true : undefined}
        onClick={photoUrl === null ? onSelect : onRemove}
        className="inline-flex items-center gap-1.5 min-h-6 text-[11px] font-semibold text-zooprimary disabled:opacity-50"
      >
        {isBusy === true ? (
          <LoadingSpinner className="w-3 h-3 border-zooprimary border-t-transparent" />
        ) : null}
        {photoUrl === null ? t('detail.addPhoto') : t('detail.removePhoto')}
      </button>
    </div>
  );
}

interface StatusMetaFieldProps {
  value: string;
  isDisabled: boolean;
  onChange: (nextStatus: string) => void;
}

function StatusMetaField({ value, isDisabled, onChange }: StatusMetaFieldProps) {
  const { t } = useTranslation('patients');
  return (
    <div className="flex items-start gap-2 min-w-0">
      <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-zoovpalelavender text-zooprimary">
        <Icon name="Sliders" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-zoomuted mb-1">
          {t('detail.status')}
        </p>
        <SearchableSelect
          inputId="patient-status"
          ariaLabel={t('detail.status')}
          value={value}
          isDisabled={isDisabled}
          options={PATIENT_CASE_STATUS_OPTIONS.map(function toOption(option) {
            return { value: option, label: t('status.' + option) };
          })}
          onChange={onChange}
        />
      </div>
    </div>
  );
}

interface MetaItemProps {
  icon: IconName;
  label: string;
  value: string;
  isHighlight?: boolean;
  isMuted?: boolean;
}

function MetaItem({ icon, label, value, isHighlight = false, isMuted = false }: MetaItemProps) {
  return (
    <div className="flex items-start gap-2 min-w-0">
      <span
        className={
          'w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ' +
          (isHighlight ? 'bg-zoovpalelavender text-zooprimary' : 'bg-zoooffwhite text-zoomuted')
        }
      >
        <Icon name={icon} />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-zoomuted">{label}</p>
        <p
          className={
            'text-sm font-semibold mt-0.5 ' +
            (isMuted ? 'text-zoomuted' : isHighlight ? 'text-zooprimary' : 'text-zooindigo')
          }
        >
          {value}
        </p>
      </div>
    </div>
  );
}
